/**
 * KD Customizer Drawer — charm-based personalization.
 *
 * Custom element <lp-pdp-kd-customizer-drawer> driven by the
 * `template.type` metaobject chain. Mirrors the Hydrogen
 * Personalization.tsx + CharmTemplate.tsx UI exactly.
 *
 * Cart properties format (matches Hydrogen):
 *   Type 1: "Disc {n} ({categoryDisplayName})" = display_name or text
 *   Type 2: "Charm Type2 Disc {n} ({categoryDisplayName})" = display_name or text
 */

if (!customElements.get('lp-pdp-kd-customizer-drawer')) {
  customElements.define(
    'lp-pdp-kd-customizer-drawer',
    class LpPdpKdCustomizerDrawer extends HTMLElement {
      constructor() {
        super();
        this._onOpenClick = this._onOpenClick.bind(this);
        this._onOverlayClick = this._onOverlayClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
        this._onAtcClick = this._onAtcClick.bind(this);
      }

      connectedCallback() {
        const wrapperId = this.dataset.formWrapperId;
        const wrapper = wrapperId ? document.getElementById(wrapperId) : null;
        this.form = wrapper?.querySelector('#lp-product-form');
        this.openBtn = wrapper?.querySelector('[data-js-open-personalize]');
        this.variantInput = this.form?.querySelector('[name="id"]');
        this.variantDataEl = this.form?.querySelector('#lp-variant-data');

        /* Portal to body for overlay stacking */
        if (!this._portaledToBody) {
          this._portaledToBody = true;
          document.body.appendChild(this);
        }

        this.overlay = this.querySelector('[data-js-personalize-overlay]');
        this.panel = this.querySelector('[data-js-personalize-panel]');
        this.closeBtn = this.querySelector('[data-js-personalize-close]');
        this.atcBtn = this.querySelector('[data-kd-drawer-atc]');
        this.atcLabel = this.querySelector('[data-kd-atc-label]');
        this.validationMsg = this.querySelector('[data-kd-validation-msg]');
        this.headerImg = this.querySelector('[data-js-drawer-product-img]');
        this.headerPrice = this.querySelector('[data-js-drawer-product-price]');

        /* Parse config */
        this.ct1Min = parseInt(this.dataset.ct1Min) || 1;
        this.ct1Max = parseInt(this.dataset.ct1Max) || 1;
        this.ct2Min = parseInt(this.dataset.ct2Min) || 0;
        this.ct2Max = parseInt(this.dataset.ct2Max) || 0;

        /* Parse variants */
        this.variants = [];
        if (this.variantDataEl) {
          try { this.variants = JSON.parse(this.variantDataEl.textContent); } catch {}
        }

        /* Find the "# of Discs" / "# of" variant option for syncing */
        this.discOptionName = null;
        this.discOptionValues = [];
        this._findDiscOption();

        /* Capture the initial disc count so we can restore it after Add-to-Cart. */
        this._initialDiscCount = null;
        if (this.discOptionRadios) {
          const initialChecked = Array.from(this.discOptionRadios).find(r => r.checked);
          if (initialChecked) {
            const match = initialChecked.value.match(/\d+/);
            if (match) this._initialDiscCount = parseInt(match[0]);
          }
        }

        /* Parse the JSON data block */
        this._parseJsonData();

        /* State */
        this.discs = [{}];
        this.discs2 = [{}];
        this.activeTab = [0, 0];
        this.activeCategory = [0, 0];
        this.activeSymbolIndex = [[-1], [-1]]; /* [type][tab][category] */
        this.emptyDisc = false;

        /* Build the charm UI */
        this._buildCharmUI();

        /* Bind events */
        if (this.openBtn) this.openBtn.addEventListener('click', this._onOpenClick);
        if (this.overlay) this.overlay.addEventListener('click', this._onOverlayClick);
        if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
        if (this.atcBtn) this.atcBtn.addEventListener('click', this._onAtcClick);

        /* Update header on variant change */
        if (this.variantInput) this.variantInput.addEventListener('change', () => this._syncHeader());
        this._syncHeader();
      }

      disconnectedCallback() {
        if (this.openBtn) this.openBtn.removeEventListener('click', this._onOpenClick);
      }

      /* ═══════════════════════════════════════
         JSON Parsing & Charm Type Resolution
         ═══════════════════════════════════════ */

      _parseJsonData() {
        let jsonEl = null;
        const wrapperId = this.dataset.formWrapperId;
        const wrapper = wrapperId ? document.getElementById(wrapperId) : null;
        if (wrapper) jsonEl = wrapper.querySelector('[id^="kd-customizer-data-"]');
        if (!jsonEl) jsonEl = document.querySelector('[id^="kd-customizer-data-"]');

        this.kdData = {};
        if (jsonEl) {
          try { this.kdData = JSON.parse(jsonEl.textContent); } catch (e) {
            console.error('[KD] Failed to parse JSON data', e);
          }
        }

        this._resolveCharmTypes();
      }

      /**
       * Read the pre-resolved charm type data from Liquid.
       * The Liquid template walks the entire metaobject chain
       * (charm_type → symbol_categories → symbols, charm_type → base)
       * and outputs fully resolved nested JSON — no GID lookups needed.
       */
      _resolveCharmTypes() {
        this.charmTypes = [null, null];

        const ct1 = this.kdData.charmType1;
        const ct2 = this.kdData.charmType2;

        if (ct1) {
          this.charmTypes[0] = {
            name: ct1.name || '',
            characterLimit: ct1.characterLimit || 20,
            base: ct1.base || null,
            stampOffsetTop: ct1.base?.stampOffsetTop ? Number(ct1.base.stampOffsetTop) : null,
            categories: (ct1.categories || []).reverse().map(cat => ({
              display_name: cat.displayName || '',
              text: cat.text === true,
              symbols: (cat.symbols || []).map(sym => ({
                display_name: sym.displayName || '',
                svg: sym.svg || '',
                category: sym.category || '',
              })),
            })),
          };
        }

        if (ct2) {
          this.charmTypes[1] = {
            name: ct2.name || '',
            characterLimit: ct2.characterLimit || 20,
            base: ct2.base || null,
            stampOffsetTop: ct2.base?.stampOffsetTop ? Number(ct2.base.stampOffsetTop) : null,
            categories: (ct2.categories || []).reverse().map(cat => ({
              display_name: cat.displayName || '',
              text: cat.text === true,
              symbols: (cat.symbols || []).map(sym => ({
                display_name: sym.displayName || '',
                svg: sym.svg || '',
                category: sym.category || '',
              })),
            })),
          };
        }
      }

      /* ═══════════════════════════════════════
         Variant Helpers
         ═══════════════════════════════════════ */

      _findDiscOption() {
        /* Look for a variant option named "# of Discs" or "# of" */
        const section = this.form?.closest('section');
        if (!section) return;
        const optionGroups = section.querySelectorAll('.lp-pdp__option-group');
        optionGroups.forEach(group => {
          const nameEl = group.querySelector('.lp-pdp__option-name');
          if (!nameEl) return;
          const name = nameEl.textContent.trim();
          if (name.includes('# of') || name.toLowerCase().includes('disc')) {
            this.discOptionName = name;
            const radios = group.querySelectorAll('input[type="radio"]');
            this.discOptionRadios = radios;
            this.discOptionValues = [...radios].map(r => r.value);
          }
        });
      }

      _syncDiscVariant(discCount) {
        /* When user adds/removes discs, update the "# of Discs" variant option */
        if (!this.discOptionRadios || this.discOptionRadios.length === 0) return;

        /* Find the option value that matches this disc count */
        const target = this.discOptionValues.find(v =>
          v.includes(String(discCount)) && v.toLowerCase().includes('disc')
        );
        if (!target) return;

        for (const radio of this.discOptionRadios) {
          if (radio.value === target) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }

      _getVariantColor() {
        /* Return hex color from color_swatch metaobject for the selected variant.
           Matches the selected variant's Color option value against the
           color_swatch metaobjects (by color_name), then returns that swatch's hex.
           If the swatch uses an image instead of a hex color (e.g. Silver),
           derive a sensible color from the variant name.
           Falls back to #f9dc9d (gold) only if nothing else matches. */
        const FALLBACK = '#f9dc9d';
        const vid = this.variantInput ? parseInt(this.variantInput.value) : null;
        if (!vid) return FALLBACK;

        const pos = parseInt(this.kdData.colorOptionPosition) || 0;
        const variantOptions = this.kdData.variantOptions || {};
        const swatches = this.kdData.colorSwatches || [];
        if (pos < 1 || !variantOptions[String(vid)] || !swatches.length) return FALLBACK;

        const opts = variantOptions[String(vid)];
        const colorValue = (opts[pos - 1] || '').toString().trim().toLowerCase();
        if (!colorValue) return FALLBACK;

        const matched = swatches.find(sw => (sw.name || '').trim().toLowerCase() === colorValue);

        /* 1. Swatch has a valid hex / rgb color — use it directly */
        const hex = matched && typeof matched.hex === 'string' ? matched.hex.trim() : '';
        if (hex) {
          if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(hex)) return hex;
          if (/^(rgb|rgba|hsl|hsla)\(/i.test(hex)) return hex;
        }

        /* 2. Swatch matched but uses an image (no hex) — derive color from the
              variant name so the disc preview still shows the correct material tone */
        if (matched) {
          return this._colorFromName(colorValue);
        }

        /* 3. No swatch matched at all — still try to derive from the name */
        return this._colorFromName(colorValue);
      }

      /**
       * Derive a sensible disc preview color from a variant color-option name.
       * Covers the common Made By Mary materials; unknown names fall back to gold.
       */
      _colorFromName(name) {
        const FALLBACK = '#f9dc9d';
        if (!name) return FALLBACK;
        const n = name.toLowerCase();

        /* Known material → color map */
        if (n.includes('silver') || n.includes('sterling'))    return '#DCDCDC';
        if (n.includes('rose gold') || n.includes('rosegold')) return '#e8b4b8';
        if (n.includes('gold'))                                return '#f9dc9d';
        if (n.includes('copper'))                              return '#b87333';
        if (n.includes('brass'))                               return '#d4a017';
        if (n.includes('platinum'))                             return '#e5e4e2';
        if (n.includes('black'))                               return '#333333';
        if (n.includes('white'))                               return '#f5f5f5';

        return FALLBACK;
      }

      _syncHeader() {
        const vid = this.variantInput ? parseInt(this.variantInput.value) : null;
        const v = vid ? this.variants.find(x => x.id === vid) : null;
        if (!v) return;
        /* Preserve any active Shoplift price-test adjustment when re-rendering
           prices; the main price block is server-rendered with the lowest
           variant price, which seeds the adjustment detection. */
        const renderedCents = this.variants.length ? Math.min(...this.variants.map(x => x.price)) : null;
        const mainPriceEl = document.querySelector('.lp-pdp__price');
        const testAdjustment = window.ShopliftPrice.adjustment(mainPriceEl, renderedCents);
        if (this.headerPrice) {
          this.headerPrice.textContent = this._formatMoney(v.price + testAdjustment);
        }
        if (mainPriceEl) {
          window.ShopliftPrice.write(mainPriceEl, v.price, (cents) => this._formatMoney(cents), renderedCents);
        }
        if (this.headerImg && v.featured_image?.src) {
          this.headerImg.src = v.featured_image.src.replace(/(\.\w+)(\?|$)/, '_200x$1$2');
        }
        /* Re-render previews with new color */
        this._renderPreviews(0);
        if (this.charmTypes[1]) this._renderPreviews(1);
      }

      _formatMoney(cents) {
        const currency = this.dataset.currency || 'USD';
        return new Intl.NumberFormat('en-US', {
          style: 'currency', currency,
          minimumFractionDigits: 0, maximumFractionDigits: 2,
        }).format(cents / 100);
      }

      /* ═══════════════════════════════════════
         Build Charm UI
         ═══════════════════════════════════════ */

      _buildCharmUI() {
        for (let t = 0; t < 2; t++) {
          const ct = this.charmTypes[t];
          const max = t === 0 ? this.ct1Max : this.ct2Max;
          const min = t === 0 ? this.ct1Min : this.ct2Min;
          if (!ct || max <= 0) continue;

          const arr = [];
          for (let i = 0; i < Math.max(min, 1); i++) arr.push({});
          if (t === 0) this.discs = arr; else this.discs2 = arr;

          this.activeSymbolIndex[t] = arr.map(() => ct.categories.map(() => -1));

          this._renderTabs(t);
          this._renderPreviews(t);
          this._renderCharmTemplate(t);
          this._updateRemoveBtn(t);
        }
        this._checkEmptyDisc();
      }

      /* ═══════════════════════════════════════
         TABS — folder-style (matches Hydrogen)
         ═══════════════════════════════════════ */

      _renderTabs(typeIdx) {
        const container = this.querySelector(`[data-kd-charm-tabs="${typeIdx}"]`);
        if (!container) return;

        const ct = this.charmTypes[typeIdx];
        const max = typeIdx === 0 ? this.ct1Max : this.ct2Max;
        const discArr = typeIdx === 0 ? this.discs : this.discs2;
        const charmName = ct?.name || '';
        const isHeart = charmName.toLowerCase().includes('heart');
        const label = isHeart ? charmName : 'Disc';

        container.innerHTML = '';

        if (max <= 1 && discArr.length <= 1) {
          container.hidden = true;
          return;
        }
        container.hidden = false;

        /* Disc tabs */
        container.setAttribute('role', 'tablist');
        container.setAttribute('aria-label', `${label} selection`);
        discArr.forEach((_, i) => {
          const tab = document.createElement('div');
          tab.className = 'kd-charm-tab' + (i === this.activeTab[typeIdx] ? ' kd-charm-tab--active' : '');
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-selected', i === this.activeTab[typeIdx] ? 'true' : 'false');
          tab.setAttribute('aria-label', `${label} ${i + 1}`);
          tab.setAttribute('tabindex', i === this.activeTab[typeIdx] ? '0' : '-1');
          const p = document.createElement('p');
          p.textContent = `${label} ${i + 1}`;
          p.setAttribute('aria-hidden', 'true');
          tab.appendChild(p);
          const activateTab = () => {
            this.activeTab[typeIdx] = i;
            this._renderTabs(typeIdx);
            this._renderPreviews(typeIdx);
            this._renderCharmTemplate(typeIdx);
          };
          tab.addEventListener('click', activateTab);
          tab.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activateTab(); }
          });
          container.appendChild(tab);
        });

        /* "Add Disc" tab */
        if (discArr.length < max) {
          const addTab = document.createElement('div');
          addTab.className = 'kd-charm-tab kd-charm-tab--add';
          addTab.setAttribute('role', 'button');
          addTab.setAttribute('tabindex', '0');
          addTab.setAttribute('aria-label', `Add ${label}`);
          const p = document.createElement('p');
          p.textContent = `Add ${label}`;
          p.setAttribute('aria-hidden', 'true');
          addTab.appendChild(p);
          addTab.addEventListener('click', () => this._addDisc(typeIdx));
          addTab.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._addDisc(typeIdx); }
          });
          container.appendChild(addTab);
        }
      }

      _addDisc(typeIdx) {
        const ct = this.charmTypes[typeIdx];
        const arr = typeIdx === 0 ? this.discs : this.discs2;
        arr.push({});
        this.activeSymbolIndex[typeIdx].push(ct.categories.map(() => -1));
        this.activeTab[typeIdx] = arr.length - 1;

        this._syncDiscVariant(arr.length);
        this._renderTabs(typeIdx);
        this._renderPreviews(typeIdx);
        this._renderCharmTemplate(typeIdx);
        this._updateRemoveBtn(typeIdx);
        this._checkEmptyDisc();
      }

      _removeDisc(typeIdx) {
        const arr = typeIdx === 0 ? this.discs : this.discs2;
        const tabIdx = this.activeTab[typeIdx];
        arr.splice(tabIdx, 1);
        this.activeSymbolIndex[typeIdx].splice(tabIdx, 1);
        this.activeTab[typeIdx] = 0;

        this._syncDiscVariant(arr.length);
        this._renderTabs(typeIdx);
        this._renderPreviews(typeIdx);
        this._renderCharmTemplate(typeIdx);
        this._updateRemoveBtn(typeIdx);
        this._checkEmptyDisc();
      }

      _updateRemoveBtn(typeIdx) {
        const removeWrap = this.querySelector(`[data-kd-remove-wrap="${typeIdx}"]`);
        if (!removeWrap) return;
        const min = typeIdx === 0 ? this.ct1Min : this.ct2Min;
        const arr = typeIdx === 0 ? this.discs : this.discs2;
        const ct = this.charmTypes[typeIdx];
        const charmName = ct?.name || '';
        const isHeart = charmName.toLowerCase().includes('heart');
        const label = isHeart ? charmName : 'Disc';

        if (arr.length > min) {
          removeWrap.hidden = false;
          const btn = removeWrap.querySelector('.kd-charm-remove-btn');
          if (btn) btn.textContent = `- REMOVE CURRENT ${label} -`;
        } else {
          removeWrap.hidden = true;
        }
      }

      /* ═════════════════
         DISC PREVIEWS 
         ═════════════════ */

      _renderPreviews(typeIdx) {
        const container = this.querySelector(`[data-kd-charm-previews="${typeIdx}"]`);
        if (!container) return;

        const discArr = typeIdx === 0 ? this.discs : this.discs2;
        const activeIdx = this.activeTab[typeIdx];
        const ct = this.charmTypes[typeIdx];
        const charmName = ct?.name || '';
        const base = ct?.base || null;
        const isOval = charmName.toLowerCase().includes('oval');
        const fillColor = this._getVariantColor();
        const bgColor = fillColor;

        container.innerHTML = '';

        discArr.forEach((disc, i) => {
          const wrap = document.createElement('div');
          wrap.className = 'kd-charm-preview' + (i === activeIdx ? ' kd-charm-preview--active' : '');

          /* Determine font family from disc category */
          const catName = (disc.key || '').toLowerCase();
          let fontFamily = 'Arial, sans-serif';
          if (catName.includes('script')) fontFamily = 'Script';
          else if (catName.includes('typewriter')) fontFamily = 'Typewriter';

          const stampOffsetTop = ct?.stampOffsetTop;

          if (base && base.svgMarkup) {
            /* ── SVG-based disc (matching Hydrogen's svgMarkup rendering) ── */
            const svgContainer = document.createElement('div');
            svgContainer.className = 'kd-charm-preview__svg-wrap';
            svgContainer.style.fontFamily = fontFamily;

            const w = base.width ? `calc(9.6em * ${base.width})` : '50px';
            const h = base.height ? `calc(9.6em * ${base.height})` : '50px';
            svgContainer.style.width = w;
            svgContainer.style.height = h;

            /* Color the SVG fill based on variant.
               Replace all occurrences of class="cls-1" and any existing fill colors
               with the variant's hex color so the disc always reflects the material. */
            let coloredSvg = base.svgMarkup
              .replace(/class="cls-1"/g, `fill="${fillColor}"`)
              .replace(/fill="#[0-9a-fA-F]{3,8}"/g, `fill="${fillColor}"`)
              .replace(/fill:\s*#[0-9a-fA-F]{3,8}/g, `fill: ${fillColor}`);
            svgContainer.innerHTML = coloredSvg;

            /* Stamp content overlay */
            const stamp = document.createElement('span');
            stamp.className = 'kd-charm-preview__stamp';
            stamp.style.fontFamily = fontFamily;
            if (stampOffsetTop != null) stamp.style.top = `${stampOffsetTop}rem`;

            if (disc.value) {
              const symCat = (typeof disc.value === 'object' ? disc.value.category : '') || '';
              const hasSvg = typeof disc.value === 'object' && disc.value.svg;
              const key = disc.key || '';
              if (symCat === 'Symbols' && hasSvg) {
                stamp.classList.add('kd-charm-preview__stamp--symbol');
                stamp.innerHTML = disc.value.svg;
              } else {
                const dn = typeof disc.value === 'object'
                  ? (disc.value.display_name || '')
                  : disc.value;
                if (!dn.includes('Blank')) {
                  if (symCat.includes('Initial')) {
                    if (key.includes('2.5mm')) stamp.style.fontSize = '20px';
                    else if (key.includes('6mm')) stamp.style.fontSize = '3em';
                    else stamp.style.fontSize = '0.75em';
                  }
                  stamp.textContent = dn;
                }
              }
            }
            svgContainer.appendChild(stamp);

            /* Top alignment dot */
            const dot = document.createElement('span');
            dot.className = 'kd-charm-preview__dot';
            dot.style.borderColor = fillColor;
            if (charmName.toLowerCase().includes('heart')) dot.style.left = '33.333%';
            svgContainer.appendChild(dot);

            wrap.appendChild(svgContainer);
          } else {
            /* ── Fallback: circle-based disc ── */
            const circle = document.createElement('div');
            circle.className = 'kd-charm-preview__circle';
            circle.style.background = bgColor;
            circle.style.borderColor = '#a4974c';
            circle.style.color = '#a4974c';
            circle.style.fontFamily = fontFamily;

            if (base && base.width) {
              circle.style.width = `calc(9.6em * ${base.width})`;
              circle.style.height = base.height ? `calc(9.6em * ${base.height})` : circle.style.width;
            } else {
              circle.style.width = isOval ? '50px' : '80px';
              circle.style.height = '80px';
            }

            /* Alignment dot */
            const dot = document.createElement('span');
            dot.className = 'kd-charm-preview__dot';
            dot.style.borderColor = '#a4974c';
            if (charmName.toLowerCase().includes('heart')) dot.style.left = '33.333%';
            circle.appendChild(dot);

            /* Stamp content */
            if (disc.value) {
              const symCat = (typeof disc.value === 'object' ? disc.value.category : '') || '';
              const hasSvg = typeof disc.value === 'object' && disc.value.svg;
              const key = disc.key || '';
              if (hasSvg) {
                const stamp = document.createElement('span');
                stamp.className = 'kd-charm-preview__stamp kd-charm-preview__stamp--symbol';
                stamp.innerHTML = disc.value.svg;
                circle.appendChild(stamp);
              } else {
                const dn = typeof disc.value === 'object'
                  ? (disc.value.display_name || '')
                  : disc.value;
                if (dn && !dn.includes('Blank')) {
                  const stamp = document.createElement('span');
                  stamp.className = 'kd-charm-preview__stamp';
                  if (symCat.includes('Initial')) {
                    if (key.includes('2.5mm')) stamp.style.fontSize = '20px';
                    else if (key.includes('6mm')) stamp.style.fontSize = '3em';
                    else stamp.style.fontSize = '0.75em';
                  }
                  if (stampOffsetTop != null) stamp.style.top = `${stampOffsetTop}rem`;
                  stamp.textContent = dn;
                  circle.appendChild(stamp);
                }
              }
            }

            wrap.appendChild(circle);
          }

          const discLabel = disc?.value
            ? (typeof disc.value === 'object' ? disc.value.display_name || `Disc ${i + 1}` : disc.value)
            : `Disc ${i + 1}, empty`;
          wrap.setAttribute('role', 'button');
          wrap.setAttribute('tabindex', '0');
          wrap.setAttribute('aria-label', `Select ${discLabel}`);
          wrap.setAttribute('aria-pressed', i === activeIdx ? 'true' : 'false');
          const selectDisc = () => {
            this.activeTab[typeIdx] = i;
            this._renderTabs(typeIdx);
            this._renderPreviews(typeIdx);
            this._renderCharmTemplate(typeIdx);
          };
          wrap.addEventListener('click', selectDisc);
          wrap.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectDisc(); }
          });
          container.appendChild(wrap);
        });
      }

      /* ═══════════════════════════════════════
         CHARM TEMPLATE (category + symbols)
         ═══════════════════════════════════════ */

      _renderCharmTemplate(typeIdx) {
        const ct = this.charmTypes[typeIdx];
        if (!ct) return;

        const el = this.querySelector(`[data-kd-charm-template="${typeIdx}"]`);
        if (!el) return;

        const categories = ct.categories;
        const activeCatIdx = this.activeCategory[typeIdx];

        /* Category selector */
        const catSelector = el.querySelector('[data-kd-category-selector]');
        const catSelect = el.querySelector('[data-kd-category-select]');

        if (categories.length > 1 && catSelector && catSelect) {
          catSelector.hidden = false;
          catSelect.innerHTML = '';
          categories.forEach((cat, i) => {
            const opt = document.createElement('option');
            opt.value = i;
            opt.textContent = cat.display_name || `Category ${i + 1}`;
            if (i === activeCatIdx) opt.selected = true;
            catSelect.appendChild(opt);
          });
          catSelect.onchange = () => {
            this.activeCategory[typeIdx] = parseInt(catSelect.value);
            this._renderCategoryPanel(typeIdx);
          };
        } else if (catSelector) {
          catSelector.hidden = true;
        }

        this._renderCategoryPanel(typeIdx);
      }

      _renderCategoryPanel(typeIdx) {
        const ct = this.charmTypes[typeIdx];
        if (!ct) return;

        const el = this.querySelector(`[data-kd-charm-template="${typeIdx}"]`);
        if (!el) return;

        const categories = ct.categories;
        const activeCatIdx = this.activeCategory[typeIdx];
        const activeTabIdx = this.activeTab[typeIdx];
        const category = categories[activeCatIdx];
        if (!category) return;

        const nameEl = el.querySelector('[data-kd-panel-category-name]');
        const selectedEl = el.querySelector('[data-kd-panel-selected-name]');
        const body = el.querySelector('[data-kd-panel-body]');
        if (!body) return;

        /* Header: category name + selected value */
        if (nameEl) nameEl.textContent = category.display_name || '';

        const discArr = typeIdx === 0 ? this.discs : this.discs2;
        const disc = discArr[activeTabIdx];
        if (selectedEl) {
          if (disc?.value) {
            const name = typeof disc.value === 'object' ? disc.value.display_name || '' : disc.value;
            selectedEl.textContent = name;
          } else {
            selectedEl.textContent = '';
          }
        }

        body.innerHTML = '';

        const hasSymbols = category.symbols && category.symbols.length > 0;
        const isTextMode = category.text === true;

        if (hasSymbols && isTextMode) {
          this._renderDropdown(body, category, typeIdx);
        } else if (hasSymbols) {
          this._renderSymbolGrid(body, category, typeIdx);
        } else {
          this._renderTextInput(body, ct, typeIdx);
        }
      }

      /* ── Symbol grid (clickable 40x40 tiles) ── */
      _renderSymbolGrid(container, category, typeIdx) {
        const activeTabIdx = this.activeTab[typeIdx];
        const activeCatIdx = this.activeCategory[typeIdx];
        const currentSymIdx = this.activeSymbolIndex[typeIdx]?.[activeTabIdx]?.[activeCatIdx] ?? -1;

        const catName = (category.display_name || '').toLowerCase();
        let fontFamily = 'Arial, sans-serif';
        if (catName.includes('script')) fontFamily = 'Script, cursive';
        else if (catName.includes('typewriter')) fontFamily = 'Typewriter, monospace';

        const grid = document.createElement('fieldset');
        grid.className = 'kd-charm-template__symbol-grid';

        const legend = document.createElement('legend');
        legend.className = 'kd-charm-template__symbol-grid-legend';
        legend.textContent = `Select a ${category.display_name || 'symbol'}`;
        grid.appendChild(legend);

        const groupName = `kd-symbol-${typeIdx}-${Math.random().toString(36).slice(2)}`;

        category.symbols.forEach((symbol, i) => {
          const radioId = `${groupName}-${i}`;
          const radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = groupName;
          radio.id = radioId;
          radio.value = i;
          radio.className = 'kd-charm-template__symbol-radio';
          if (i === currentSymIdx) radio.checked = true;

          const dn = symbol.display_name || `Symbol ${i + 1}`;
          radio.setAttribute('aria-label', dn.includes('Blank') ? 'Blank' : dn);

          const tile = document.createElement('label');
          tile.htmlFor = radioId;
          tile.className = 'kd-charm-template__symbol-tile' +
            (i === currentSymIdx ? ' kd-charm-template__symbol-tile--active' : '');

          if (symbol.svg) {
            const svgWrap = document.createElement('span');
            svgWrap.className = 'kd-charm-template__symbol-svg';
            svgWrap.setAttribute('aria-hidden', 'true');
            svgWrap.innerHTML = symbol.svg;
            tile.appendChild(svgWrap);
          } else {
            const textSpan = document.createElement('span');
            textSpan.className = 'kd-charm-template__symbol-text';
            textSpan.style.fontFamily = fontFamily;
            textSpan.setAttribute('aria-hidden', 'true');
            textSpan.textContent = dn.includes('Blank') ? '' : dn;
            tile.appendChild(textSpan);
          }

          radio.addEventListener('change', () => this._selectSymbol(typeIdx, i));
          grid.appendChild(radio);
          grid.appendChild(tile);
        });

        container.appendChild(grid);
      }

      /* ── Dropdown (text=true mode) ── */
      _renderDropdown(container, category, typeIdx) {
        const activeTabIdx = this.activeTab[typeIdx];
        const activeCatIdx = this.activeCategory[typeIdx];
        const symIdx = this.activeSymbolIndex[typeIdx]?.[activeTabIdx]?.[activeCatIdx] ?? -1;

        const select = document.createElement('select');
        select.className = 'kd-charm-template__symbol-select';
        select.setAttribute('aria-label', `Select ${category.display_name || 'symbol option'}`);

        const ph = document.createElement('option');
        ph.value = '';
        ph.textContent = 'Select an option';
        ph.disabled = true;
        if (symIdx < 0) ph.selected = true;
        select.appendChild(ph);

        category.symbols.forEach((symbol, i) => {
          const opt = document.createElement('option');
          opt.value = i;
          opt.textContent = symbol.display_name || `Symbol ${i + 1}`;
          if (i === symIdx) opt.selected = true;
          select.appendChild(opt);
        });

        select.addEventListener('change', () => this._selectSymbol(typeIdx, parseInt(select.value)));
        container.appendChild(select);
      }

      /* ── Free text input ── */
      _renderTextInput(container, charmType, typeIdx) {
        const activeTabIdx = this.activeTab[typeIdx];
        const discArr = typeIdx === 0 ? this.discs : this.discs2;
        const disc = discArr[activeTabIdx];
        const charLimit = charmType.characterLimit || 20;

        const wrap = document.createElement('div');
        wrap.className = 'kd-charm-template__text-wrap';

        const categories = charmType.categories;
        const activeCatIdx = this.activeCategory[typeIdx];
        const inputLabel = categories?.[activeCatIdx]?.display_name || 'Text engraving';

        const input = document.createElement('input');
        input.className = 'kd-charm-template__text-input';
        input.type = 'text';
        input.maxLength = charLimit;
        input.autocomplete = 'off';
        input.setAttribute('aria-label', `${inputLabel} (max ${charLimit} characters)`);
        input.value = typeof disc?.value === 'string' ? disc.value : '';

        const counter = document.createElement('span');
        counter.className = 'kd-charm-template__char-counter';
        counter.setAttribute('aria-live', 'polite');
        counter.setAttribute('aria-atomic', 'true');
        counter.textContent = `${input.value.length}/${charLimit}`;

        input.addEventListener('input', () => {
          counter.textContent = `${input.value.length}/${charLimit}`;
          const categories = charmType.categories;
          const activeCatIdx = this.activeCategory[typeIdx];
          const cat = categories[activeCatIdx];
          const catName = cat?.display_name || 'Text';

          discArr[activeTabIdx] = { key: catName, value: input.value };
          this._renderPreviews(typeIdx);
          this._updateSelectedName(typeIdx);
          this._checkEmptyDisc();
        });

        wrap.appendChild(input);
        wrap.appendChild(counter);
        container.appendChild(wrap);
      }

      _selectSymbol(typeIdx, symbolIndex) {
        const ct = this.charmTypes[typeIdx];
        if (!ct) return;
        const activeTabIdx = this.activeTab[typeIdx];
        const activeCatIdx = this.activeCategory[typeIdx];
        const category = ct.categories[activeCatIdx];
        if (!category) return;
        const symbol = category.symbols[symbolIndex];
        if (!symbol) return;

        if (!this.activeSymbolIndex[typeIdx][activeTabIdx]) {
          this.activeSymbolIndex[typeIdx][activeTabIdx] = ct.categories.map(() => -1);
        }
        this.activeSymbolIndex[typeIdx][activeTabIdx][activeCatIdx] = symbolIndex;

        const discArr = typeIdx === 0 ? this.discs : this.discs2;
        discArr[activeTabIdx] = {
          key: category.display_name || '',
          value: symbol,
        };

        this._renderCategoryPanel(typeIdx);
        this._renderPreviews(typeIdx);
        this._checkEmptyDisc();
      }

      _updateSelectedName(typeIdx) {
        const el = this.querySelector(`[data-kd-charm-template="${typeIdx}"]`);
        if (!el) return;
        const selectedEl = el.querySelector('[data-kd-panel-selected-name]');
        if (!selectedEl) return;
        const discArr = typeIdx === 0 ? this.discs : this.discs2;
        const disc = discArr[this.activeTab[typeIdx]];
        if (disc?.value) {
          selectedEl.textContent = typeof disc.value === 'object' ? disc.value.display_name || '' : disc.value;
        } else {
          selectedEl.textContent = '';
        }
      }

      /* ═══════════════════════════════════════
         emptyDisc check (matches Hydrogen)
         ═══════════════════════════════════════ */

      _checkEmptyDisc() {
        /* A disc is "empty" if no stamp has been selected yet.
           Every disc must have a key+value (stamp selection) for ATC to be enabled.
           Only check type 2 if it actually exists (ct2Max > 0). */
        const allType1Filled = this.discs.every(d => d.key && d.key !== '' && d.value);
        const allType2Filled = this.ct2Max > 0
          ? this.discs2.every(d => d.key && d.key !== '' && d.value)
          : true;
        const hasEmpty = !allType1Filled || !allType2Filled;
        this.emptyDisc = hasEmpty;
        if (this.atcBtn) this.atcBtn.disabled = hasEmpty;
      }

      /* ═══════════════════════════════════════
         Open / Close
         ═══════════════════════════════════════ */

      _onOpenClick(e) {
        if (this.openBtn && this.openBtn.dataset.lpBisNotify === 'true') return;
        e.preventDefault();
        this.open();
      }

      _getFocusableElements() {
        return Array.from(
          this.panel.querySelectorAll(
            'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
          )
        ).filter(el => !el.closest('[hidden]') && el.offsetParent !== null);
      }

      open() {
        this.setAttribute('data-state', 'open');
        this.removeAttribute('inert');
        this.setAttribute('aria-hidden', 'false');
        if (this.openBtn) this.openBtn.setAttribute('aria-expanded', 'true');
        document.body.style.setProperty('--lp-pdp-drawer-scrollbar-width', `${window.innerWidth - document.documentElement.clientWidth}px`);
        document.body.classList.add('lp-pdp-personalize-drawer-open');
        document.addEventListener('keydown', this._onKeydown);
        /* Re-sync header + disc colors with current variant selection */
        this._syncHeader();
        this.closeBtn?.focus();
      }

      close() {
        this.removeAttribute('data-state');
        this.setAttribute('inert', '');
        this.setAttribute('aria-hidden', 'true');
        if (this.openBtn) this.openBtn.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('lp-pdp-personalize-drawer-open');
        document.removeEventListener('keydown', this._onKeydown);
        this.openBtn?.focus();
      }

      _onOverlayClick(e) {
        if (e.target === this.overlay) this.close();
      }

      _onKeydown(e) {
        if (e.key === 'Escape') {
          this.close();
          return;
        }
        if (e.key !== 'Tab') return;
        const focusable = this._getFocusableElements();
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }

      /* ═══════════════════════════════════════
         Validation
         ═══════════════════════════════════════ */

      _validate() {
        const errors = [];

        if (this.charmTypes[0] && this.ct1Max > 0) {
          const filled = this.discs.filter(d => d.key !== undefined && d.key !== '').length;
          if (filled < this.ct1Min) {
            errors.push(`Please select at least ${this.ct1Min} disc${this.ct1Min > 1 ? 's' : ''}.`);
          }
        }

        if (this.charmTypes[1] && this.ct2Max > 0) {
          const filled = this.discs2.filter(d => d.key !== undefined && d.key !== '').length;
          if (filled < this.ct2Min) {
            errors.push(`Please select at least ${this.ct2Min} disc${this.ct2Min > 1 ? 's' : ''} for Charm Type 2.`);
          }
        }

        return errors;
      }

      /* ═══════════════════════════════════════
         Build Line Item Properties
         ═══════════════════════════════════════ */

      _buildLineItemProperties() {
        const properties = {};

        /* Type 1: "Disc {n} ({categoryDisplayName})" */
        this.discs.forEach((disc, i) => {
          if (disc.value) {
            const dn = typeof disc.value === 'object' ? disc.value.display_name || '' : disc.value;
            if (dn) properties[`Disc ${i + 1} (${disc.key})`] = dn;
          }
        });

        /* Type 2: "Charm Type2 Disc {n} ({categoryDisplayName})" */
        this.discs2.forEach((disc, i) => {
          if (disc.value) {
            const dn = typeof disc.value === 'object' ? disc.value.display_name || '' : disc.value;
            if (dn) properties[`Charm Type2 Disc ${i + 1} (${disc.key})`] = dn;
          }
        });

        /* Shoplift price test: carry the test-assignment property Shoplift
           wrote into the product form so its cart transform can reprice the
           line. */
        const slptInput = this.form?.querySelector('input[name="properties[_slpt]"]');
        if (slptInput?.value) properties['_slpt'] = slptInput.value;

        /* Personalized flag: this drawer replaces the form submit, so the
           form's hidden input never posts on its own. The cart drawer's
           delivery estimate reads it to pick the personalized lead times. */
        const personalizedInput = this.form?.querySelector('input[name="properties[_personalized]"]');
        if (personalizedInput?.value) properties['_personalized'] = personalizedInput.value;

        return properties;
      }

      /* ═══════════════════════════════════════
         Add to Cart
         ═══════════════════════════════════════ */

      _onAtcClick() {
        const errors = this._validate();
        if (errors.length > 0) {
          if (this.validationMsg) {
            this.validationMsg.textContent = errors[0];
            this.validationMsg.hidden = false;
          }
          return;
        }
        if (this.validationMsg) this.validationMsg.hidden = true;

        const variantId = this.variantInput ? parseInt(this.variantInput.value) : null;
        if (!variantId) return;

        const properties = this._buildLineItemProperties();
        const body = { items: [{ id: variantId, quantity: 1, properties }] };

        /* Loading state */
        if (this.atcBtn) this.atcBtn.disabled = true;
        if (this.atcLabel) {
          this.atcLabel.dataset.originalText = this.atcLabel.textContent;
          this.atcLabel.innerHTML = '<span class="kd-drawer__loading-dots"><span></span><span></span><span></span></span>';
        }
        this.atcBtn?.classList.add('kd-drawer__atc-btn--loading');

        const done = (ok) => {
          if (ok) {
            if (this.atcLabel) this.atcLabel.textContent = 'Added To Cart';
            setTimeout(() => {
              if (this.atcLabel) this.atcLabel.textContent = this.atcLabel.dataset.originalText || 'Add to cart';
              if (this.atcBtn) this.atcBtn.disabled = false;
              this.atcBtn?.classList.remove('kd-drawer__atc-btn--loading');
            }, 1500);
            this.close();
            /* Reset */
            this.discs = this.discs.map(() => ({}));
            this.discs2 = this.discs2.map(() => ({}));
            this.activeTab = [0, 0];
            this.activeCategory = [0, 0];
            this._buildCharmUI();
            /* Restore the original variant (and prices) — _syncDiscVariant fires a
               change event on the variant select, which cascades into _syncHeader
               and resets both the drawer price and the main .lp-pdp__price. */
            if (this._initialDiscCount !== null) {
              this._syncDiscVariant(this._initialDiscCount);
            }
          } else {
            if (this.atcLabel) this.atcLabel.textContent = 'Try again';
            setTimeout(() => {
              if (this.atcLabel) this.atcLabel.textContent = this.atcLabel.dataset.originalText || 'Add to cart';
              if (this.atcBtn) this.atcBtn.disabled = false;
              this.atcBtn?.classList.remove('kd-drawer__atc-btn--loading');
            }, 2500);
          }
        };

        if (window.liquidAjaxCart?.add) {
          window.liquidAjaxCart.add(body, { lastCallback: () => done(true) });
        } else {
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          })
            .then(r => { if (!r.ok) throw new Error(); return r.json(); })
            .then(() => {
              if (window.liquidAjaxCart?.update) window.liquidAjaxCart.update();
              done(true);
            })
            .catch(() => done(false));
        }
      }
    }
  );
}
